import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightLeft, Check, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../context/OrganizationContext';
import api from '../../services/api';
import i18n from '../../i18n/config';
import { getOrgIcon, getOrgColorClass } from './orgStyles';


/**
 * Tanlangan obyektlarni (hujjat / kategoriya / Gantt loyiha) boshqa tashkilotga ko'chirish modali.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - itemType: 'documents' | 'categories' | 'gantt_projects'
 *  - items: [{ id, title }]
 *  - onMoved: (targetOrg, movedCount) => void
 */
export default function MoveToOrgModal({ open, onClose, itemType, items = [], onMoved }) {
    const { t } = useTranslation();
    const { organizations, currentOrgId, getLocalizedName, refreshOrganizations } = useOrganization();
    const [targetId, setTargetId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const lang = i18n.language || 'uz';

    const targets = organizations.filter(o => o.id !== currentOrgId);

    useEffect(() => {
        if (open) {
            setTargetId(targets.length === 1 ? targets[0].id : null);
            setError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    async function handleMove() {
        if (!targetId || items.length === 0) return;
        setSaving(true);
        setError('');
        try {
            const { data } = await api.post('/organizations/move-items', {
                item_type: itemType,
                ids: items.map(i => i.id),
                target_organization_id: targetId,
            });
            const targetOrg = organizations.find(o => o.id === targetId);
            refreshOrganizations?.();
            onMoved?.(targetOrg, data.moved);
            onClose();
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || t('organizations.move_error', "Ko'chirishda xatolik"));
        } finally {
            setSaving(false);
        }
    }

    const typeLabel = t(`organizations.item_type_${itemType}`);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    onClick={() => !saving && onClose()}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: 10 }}
                        transition={{ duration: 0.18 }}
                        onClick={e => e.stopPropagation()}
                        className="bg-surface w-full max-w-md rounded-2xl shadow-xl border border-border flex flex-col max-h-[85vh]"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-border flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <ArrowRightLeft className="w-5 h-5 text-brand" />
                                    {t('organizations.move_title', "Tashkilotga ko'chirish")}
                                </h3>
                                <p className="text-xs text-muted mt-1">
                                    {t('organizations.move_subtitle', '{{count}} ta {{type}} tanlandi', { count: items.length, type: typeLabel })}
                                </p>
                            </div>
                            <button type="button" onClick={onClose} disabled={saving} className="p-1.5 rounded-lg hover:bg-surface-2 text-muted">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 overflow-y-auto space-y-4">
                            {/* Selected items preview */}
                            <div className="max-h-28 overflow-y-auto rounded-xl border border-border bg-surface-2/50 px-3 py-2 space-y-1">
                                {items.map(i => (
                                    <div key={i.id} className="text-xs text-secondary truncate">• {i.title}</div>
                                ))}
                            </div>

                            {/* Target orgs */}
                            <div>
                                <div className="text-xs font-bold text-muted uppercase tracking-wider mb-2">
                                    {t('organizations.move_target', 'Qaysi tashkilotga?')}
                                </div>
                                {targets.length === 0 ? (
                                    <p className="text-sm text-muted py-3 text-center">
                                        {t('organizations.move_no_targets', "Ko'chirish uchun boshqa tashkilot yo'q")}
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {targets.map(org => {
                                            const Icon = getOrgIcon(org);
                                            const color = getOrgColorClass(org);
                                            const selected = targetId === org.id;
                                            return (
                                                <button
                                                    key={org.id}
                                                    type="button"
                                                    onClick={() => setTargetId(org.id)}
                                                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${selected
                                                        ? 'border-brand bg-brand/5 ring-2 ring-brand/20'
                                                        : 'border-border hover:bg-surface-2'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${color}`}>
                                                            {org.logo_url
                                                                ? <img src={org.logo_url} alt="" className="w-5 h-5 object-contain" />
                                                                : <Icon className="w-4 h-4" />}
                                                        </div>
                                                        <span className="text-sm font-semibold text-primary truncate">{getLocalizedName(org, lang)}</span>
                                                    </div>
                                                    {selected && <Check className="w-4 h-4 text-brand flex-shrink-0" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {itemType === 'categories' && (
                                <p className="text-xs text-muted">
                                    {t('organizations.move_categories_hint', "Kategoriya bilan birga uning barcha subkategoriyalari ham ko'chiriladi.")}
                                </p>
                            )}

                            {error && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs border border-red-200 dark:border-red-800">
                                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                    {error}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-5 border-t border-border flex justify-end gap-2 bg-surface-2/30">
                            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg font-semibold text-secondary hover:bg-surface-2">
                                {t('common.cancel', 'Bekor qilish')}
                            </button>
                            <button
                                type="button"
                                onClick={handleMove}
                                disabled={!targetId || saving}
                                className="flex items-center gap-2 px-4 py-2 bg-brand text-inverse rounded-lg font-semibold disabled:opacity-50"
                            >
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                                {t('organizations.move_button', "Ko'chirish")}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
